import type { ReactNode } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const ChoreographerModal = ({ children, onClose, label, width }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width={width} label={label}>
			{children}
		</Modal>
	);
};

type UploadErrorPopupComponentProps = {
	onSubmit: () => void;
	onClose: () => void;
	headText?: ReactNode;
	bodyText?: ReactNode;
	submitText?: ReactNode;
	closeText?: ReactNode;
	modalWidth?: number | string;
	hideCloseAction?: boolean;
	suppressSubmitAnalytics?: boolean;
};

export const i18n: {
	uploadErrorAriaLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	uploadErrorAriaLabel: {
		id: 'import-from-sources.upload-error.aria-label',
		defaultMessage: 'Upload Error Popup',
		description:
			'The text is used as the accessible aria-label for the Upload Error Popup dialog. It is read by screen readers to identify the popup when an upload error occurs during the import process.',
	},
});

export const UploadErrorPopup: React.FC<UploadErrorPopupComponentProps> = ({
	onSubmit,
	onClose,
	headText,
	bodyText,
	submitText,
	closeText,
	modalWidth,
	hideCloseAction = false,
	suppressSubmitAnalytics = false,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleCloseClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'unableToImportErrorModal',
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'closeImportError',
			},
		}).fire();
		return onClose();
	};

	const handleReimportClick = () => {
		if (!suppressSubmitAnalytics) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'unableToImportErrorModal',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'selectDifferentDoc',
				},
			}).fire();
		}
		onSubmit();
	};

	return (
		<ChoreographerModal
			onClose={onClose}
			width={modalWidth || 'medium'}
			label={formatMessage(i18n.uploadErrorAriaLabel)}
		>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance="danger">{headText}</ModalTitle>
			</ModalHeader>
			<ModalBody>{bodyText}</ModalBody>
			<ModalFooter>
				<>
					{!hideCloseAction && (
						<Button
							appearance="subtle"
							onClick={handleCloseClick}
							testId="upload-error-popup-close"
						>
							{closeText}
						</Button>
					)}
					<Button
						appearance="primary"
						onClick={handleReimportClick}
						testId="upload-error-popup-submit"
					>
						{submitText}
					</Button>
				</>
			</ModalFooter>
		</ChoreographerModal>
	);
};
